import React, { useState, useRef, useEffect } from 'react';
import { Card, CardBody, Container, Row, Col, Button,Alert } from 'reactstrap';
import { Activity, Loader, CheckCircle, XCircle,
AlertTriangle,
 } from 'lucide-react';
import axios from 'axios';
import OverwriteManagerModal from "../Dashboard/Modals/OverwriteManagerModal"


const BASE_URL = process.env.REACT_APP_BASE_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const ENV_RESOURCE = process.env.REACT_APP_ENV_RESOURCE;

const NumberWheel = ({ value, onChange, min, max, label, color }) => {
    const [currentValue, setCurrentValue] = useState(value);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [valueAtDragStart, setValueAtDragStart] = useState(null);
    const itemHeight = 50;
    const numbers = Array.from(
        { length: Math.floor((max - min) / 2) + 1 }, 
        (_, i) => min + (i * 2)
    );
    const sensitivity = 5; // Lower value = more sensitive

    // Handle Mouse/Touch Events
    const handleStartDrag = (clientY) => {
        setIsDragging(true);
        setDragStart(clientY);
        setValueAtDragStart(currentValue);
    };

    const handleDrag = (clientY) => {
        if (!isDragging || dragStart === null || valueAtDragStart === null) return;

        const delta = (dragStart - clientY) * sensitivity;
        const valueChange = Math.round(delta / itemHeight)* 2;;
        const newValue = Math.max(
            min, 
            Math.min(max, Math.round((valueAtDragStart + valueChange) / 2) * 2)
        );
        if (newValue !== currentValue) {
            setCurrentValue(newValue);
            onChange(newValue);
        }
    };

    const handleEndDrag = () => {
        setIsDragging(false);
        setDragStart(null);
        setValueAtDragStart(null);
    };

    // Handle Mouse Wheel
    const handleWheel = (e) => {
        e.preventDefault();
        const direction = e.deltaY > 0 ? -2 : 2;
        const newValue = Math.max(min, Math.min(max, currentValue + direction));
        setCurrentValue(newValue);
        onChange(newValue);
    };

    // Handle Keyboard
    const handleKeyDown = (e) => {
        let newValue = currentValue;
        switch (e.key) {
            case 'ArrowUp':
                newValue = Math.min(max, currentValue + 2);
                break;
            case 'ArrowDown':
                newValue = Math.max(min, currentValue - 2);
                break;
            case 'PageUp':
                newValue = Math.min(max, currentValue + 20);
                break;
            case 'PageDown':
                newValue = Math.max(min, currentValue - 20);
                break;
            case 'Home':
                newValue = max;
                break;
            case 'End':
                newValue = min;
                break;
            default:
                return;
        }
        e.preventDefault();
        setCurrentValue(newValue);
        onChange(newValue);
    };

    // Update current value when prop changes
    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    const getItemStyle = (num) => {
        const distance = Math.abs(num - currentValue);
        const opacity = Math.max(0.2, 1 - (distance * 0.1));
        const scale = Math.max(0.85, 1 - (distance * 0.05));
        const blur = distance > 0 ? `blur(${distance}px)` : 'none';

        return {
            height: `${itemHeight}px`,
            lineHeight: `${itemHeight}px`,
            opacity,
            transform: `scale(${scale}) translateZ(0)`,
            filter: blur,
            fontWeight: distance === 0 ? '600' : '400',
            fontSize: distance === 0 ? '1.5rem' : '1.2rem',
            color: distance === 0 ? color : '#64748B',
            textShadow: distance === 0 ? `0 0 10px ${color}40` : 'none',
            transition: isDragging ? 'none' : 'all 0.15s ease'
        };
    };

    // Show more numbers in the viewport
    const visibleNumbers = numbers.filter(num =>
        Math.abs(num - currentValue) <= 20
    );

    const viewportHeight = 250;
    const translateY = -((currentValue - min) / 2 * itemHeight) + (viewportHeight / 2) - (itemHeight / 2);

    // Ensure values are always visible within viewport
    const paddingOffset = viewportHeight / 2 - itemHeight / 2;
    const topLimit = -(max - min) / 2 * itemHeight + paddingOffset;
    const bottomLimit = paddingOffset;
    const adjustedTranslateY = Math.max(topLimit, Math.min(bottomLimit, translateY));

    useEffect(() => {
        const wheel = document.querySelector('.number-wheel');

        const nonPassiveOptions = { passive: false };

        const handleTouchStart = (e) => {
            e.preventDefault();
            handleStartDrag(e.touches[0].clientY);
        };

        const handleTouchMove = (e) => {
            e.preventDefault();
            handleDrag(e.touches[0].clientY);
        };

        wheel.addEventListener('touchstart', handleTouchStart, nonPassiveOptions);
        wheel.addEventListener('touchmove', handleTouchMove, nonPassiveOptions);

        return () => {
            wheel.removeEventListener('touchstart', handleTouchStart, nonPassiveOptions);
            wheel.removeEventListener('touchmove', handleTouchMove, nonPassiveOptions);
        };
    }, []);


    return (
        <div className="number-wheel-container" style={{ textAlign: 'center' }}>
            <div className="wheel-label mb-2" style={{
                color: '#94A3B8',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontWeight: '500'
            }}>
                {label}
            </div>
            <div
                tabIndex="0"
                role="spinbutton"
                aria-valuenow={currentValue}
                aria-valuemin={min}
                aria-valuemax={max}
                className="number-wheel"
                style={{
                    height: '250px',
                    width: '100px',
                    overflow: 'hidden',
                    position: 'relative',
                    backgroundColor: '#1E293B',
                    borderRadius: '16px',
                    margin: '0 auto',
                    boxShadow: `
            0 20px 25px -5px rgba(0,0,0,0.3),
            0 8px 10px -6px rgba(0,0,0,0.2),
            inset 0 2px 4px rgba(255,255,255,0.1),
            inset 0 -2px 4px rgba(0,0,0,0.2)
          `,
                    cursor: 'ns-resize',
                    perspective: '1000px'
                }}
                onMouseDown={(e) => handleStartDrag(e.clientY)}
                onMouseMove={(e) => handleDrag(e.clientY)}
                onMouseUp={handleEndDrag}
                onMouseLeave={handleEndDrag}
                onTouchStart={(e) => {
                    handleStartDrag(e.touches[0].clientY);
                }}
                onTouchMove={(e) => {
                    handleDrag(e.touches[0].clientY); // No preventDefault here
                }}
                onTouchEnd={(e) => {
                    e.preventDefault();
                    handleEndDrag();
                }}
                onWheel={handleWheel}
                onKeyDown={handleKeyDown}
            >
                <div className="wheel-gradient-top" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '120px',
                    background: 'linear-gradient(to bottom, #1E293B 40%, transparent)',
                    zIndex: 2,
                    pointerEvents: 'none'
                }} />
                <div className="wheel-gradient-bottom" style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '120px',
                    background: 'linear-gradient(to top, #1E293B 40%, transparent)',
                    zIndex: 2,
                    pointerEvents: 'none'
                }} />
                <div
                    className="wheel-values"
                    style={{
                        position: 'absolute',
                        width: '100%',
                        transform: `translateY(${adjustedTranslateY}px)`,
                        transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                        textAlign: 'center',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {numbers.map((num) => (
                        <div key={num} style={getItemStyle(num)}>
                            {num}
                        </div>
                    ))}
                </div>
                <div
                    className="wheel-highlight"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '10px',
                        right: '10px',
                        height: '50px',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        pointerEvents: 'none',
                        zIndex: 1
                    }}
                />
            </div>
        </div>
    );
};

const BloodPressureAnnotation = () => {
    const [systolic, setSystolic] = useState(120);
    const [diastolic, setDiastolic] = useState(80);
    const [connectionId, setConnectionId] = useState(null);
    const [measurementDetails, setMeasurementDetails] = useState(null);
    const [patientDetails, setPatientDetails] = useState(null);
    const [status, setStatus] = useState('connecting'); // connecting, waiting, measuring, success, error
    const [showResult, setShowResult] = useState(false);
    const [resultType, setResultType] = useState(null);
    const [showCancelledAlert, setShowCancelledAlert] = useState(false);
    const [overwriteManagerModalOpen, setOverwriteManagerModalOpen] = useState(false)
      const [closeYourself, setCloseYourself] = useState(false)
    
    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
        };
    }, []);
    useEffect(() => {
        connectWebSocket();
        return () => {
            clearTimeout(reconnectTimeoutRef.current);
            socketRef.current?.close();
        };
    }, []);
    const attemptReconnect = () => {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
            console.log("Attempting to reconnect...");
            connectWebSocket();
        }, 2000);
    };

    const handleSubmit = async () => {
        if (!measurementDetails) return;

        try {
            setStatus('waiting_others');
            const res = await axios.post(`${BASE_URL}web/annotateBloodPressure`, {
                annotateSessionId: measurementDetails.annotatingSessionId,
                recordingId: measurementDetails.recordingId,
                patientId: measurementDetails.patientId,
                systolic: systolic,
                diastolic: diastolic
            });
            if (res.status !== 200) {
                setStatus('measuring');
            }
            
        } catch (error) {
            console.error("Error submitting blood pressure:", error);
        }
    };

    const handleSessionLeft = () => {
        setMeasurementDetails(null);
        setPatientDetails(null);
        setStatus('waiting');
        setShowResult(false);
        setSystolic(120);
        setDiastolic(80);
        setShowCancelledAlert(true);
        
        // Hide the cancelled alert after 3 seconds
        setTimeout(() => {
            setShowCancelledAlert(false);
        }, 3000);
    };
    const handleOverwriteConfirm = () => {
        if (socketRef.current) {
          socketRef.current.close()
        }
        setOverwriteManagerModalOpen(false)
        connectWebSocket()
      }

      const openClosingTab = closeMessage => {
        setCloseYourself(closeMessage)
        setOverwriteManagerModalOpen(true)
      }

    const connectWebSocket = () => {
        const authToken = localStorage.getItem("accessToken_" + ENV_RESOURCE)?.replace(/"/g, "");
        const connectionUrl = `${SOCKET_URL}?Auth=${authToken}&Type=Manager&DeviceId=Web&Mode=All`

        const socket = new WebSocket(connectionUrl);
        socketRef.current = socket;

        socket.addEventListener("open", () => {
            console.log("WebSocket connection established");
            setStatus('waiting');
            clearTimeout(reconnectTimeoutRef.current);
        });

        socket.addEventListener("message", async (event) => {
            const message = JSON.parse(event.data);
            console.log("WebSocket message received:", message);

            if (message.type === "CONNECTION_ID") {
                setConnectionId(message.connectionId);
                try {
                    await axios.get(`${BASE_URL}web/checkAnnotatingSpot`);
                } catch (error) {
                    console.error("Error checking annotating spot:", error);
                }
            }
            if (message.sender === "Backend" && message.type === "MORE_MANAGER") {
                openClosingTab(false)
              }
              if (message.sender === "Backend" && message.type === "CLOSE_YOURSELF") {
                openClosingTab(true)
              }
            if (message.type === "ANNOTATING_SESSION") {
                setMeasurementDetails(message.data);
                setStatus('measuring');
                try {
                    const response = await axios.post(`${BASE_URL}web/annotatingDetails`, {
                        patientId: message.data.patientId,
                        stationId: message.data.stationId
                    });
                    setPatientDetails(response.data);
                } catch (error) {
                    console.error("Error fetching annotating details:", error);
                }
            }
            if (message.type === "SESSION_LEFT") {
                console.log("Session was cancelled by manager");
                handleSessionLeft();
                return;
            }

            if (message.type === "ANNOTATING_DONE") {
                setShowResult(true);
                setSystolic(120);
                setDiastolic(80);
                setResultType(message.data.accepted);
                setStatus('waiting');
                setPatientDetails(null);
                setMeasurementDetails(null);
                setTimeout(() => {
                    setShowResult(false);
                }, 4000);
            }
        });

        socket.addEventListener("error", () => {
            console.log("WebSocket error");
            attemptReconnect();
        });

        socket.addEventListener("close", () => {
            console.log("WebSocket connection closed");
            attemptReconnect();
        });
    };


    const renderMeasurement = () => (
        <>
            <div className="text-center mb-2">
                <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                    Measuring for
                </small>
                <h6 className="mb-0 mt-1" style={{ 
                    fontSize: '16px',
                    background: 'linear-gradient(45deg, #60A5FA, #A78BFA)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}>
                    {patientDetails?.patientName || 'Loading...'}
                </h6>
                <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                    Station: {patientDetails?.stationName || 'Loading...'}
                </small>
            </div>

            <Row className="g-2 justify-content-center">
                <Col xs={5}>
                    <NumberWheel
                        value={systolic}
                        onChange={setSystolic}
                        min={0}
                        max={300}
                        label="Systolic"
                        color="#60A5FA"
                    />
                </Col>
                <Col xs={5}>
                    <NumberWheel
                        value={diastolic}
                        onChange={setDiastolic}
                        min={0}
                        max={230}
                        label="Diastolic"
                        color="#A78BFA"
                    />
                </Col>
            </Row>

            <div className="text-center my-2 py-2 px-2" style={{
                background: 'rgba(96, 165, 250, 0.1)',
                borderRadius: '6px',
            }}>
                <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    background: 'linear-gradient(45deg, #60A5FA, #A78BFA)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}>
                    {systolic}/{diastolic}
                    <small style={{ 
                        fontSize: '10px',
                        color: '#94A3B8',
                        marginLeft: '4px',
                        WebkitTextFillColor: '#94A3B8'
                    }}>
                        mmHg
                    </small>
                </div>
            </div>

            <Button
                color="primary"
                size="sm"
                block
                onClick={handleSubmit}
                style={{
                    background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                    border: 'none',
                    fontSize: '14px',
                    padding: '8px',
                    marginTop: '8px'
                }}
            >
                Submit Reading
            </Button>
        </>
    );



    const renderResult = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: '2rem'
        }}>
            {resultType ? (
                <>
                    <CheckCircle size={80} style={{ color: '#10B981', marginBottom: '1rem' }} />
                    <h5 style={{ color: '#E2E8F0', marginBottom: '0.5rem' }}>Measurement Accepted</h5>
                </>
            ) : (
                <>
                    <XCircle size={80} style={{ color: '#EF4444', marginBottom: '1rem' }} />
                    <h5 style={{ color: '#E2E8F0', marginBottom: '0.5rem' }}>Measurement Rejected</h5>
                </>
            )}
        </div>
    );

    const renderWaitingState = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: '2rem'
        }}>
             {showCancelledAlert && (
                <Alert 
                    className="mb-3"
                    style={{
                        backgroundColor: '#DC2626',
                        color: 'white',
                        fontSize: '12px',
                        padding: '8px',
                        border: 'none'
                    }}
                >
                    <div className="d-flex align-items-center justify-content-center">
                        <AlertTriangle size={14} className="me-2" />
                        Session cancelled by manager
                    </div>
                </Alert>
            )}
            <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#1E293B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
            }}>
                {status === 'connecting' ? (
                    <Loader className="animate-spin" size={40} style={{ color: '#60A5FA' }} />
                ) : (
                    <Activity size={40} style={{ color: '#60A5FA' }} />
                )}
            </div>
            <h3 style={{
                color: '#E2E8F0',
                fontSize: '1.25rem',
                marginBottom: '0.75rem',
                textAlign: 'center'
            }}>
                {status === 'connecting' ? 'Connecting to server...' : 'Waiting for measurement session'}
            </h3>
            <p style={{
                color: '#94A3B8',
                textAlign: 'center',
                fontSize: '0.875rem',
                maxWidth: '280px',
                margin: '0 auto'
            }}>
                {status === 'connecting'
                    ? 'Establishing secure connection...'
                    : 'Please wait while the manager initiates a measurement session'}
            </p>
        </div>
    );

    const renderWaitingOthers = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: '2rem'
        }}>
            <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#1E293B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
            }}>
                <Loader className="animate-spin" size={40} style={{ color: '#A78BFA' }} />
            </div>
            <h3 style={{
                color: '#E2E8F0',
                fontSize: '1.25rem',
                marginBottom: '0.75rem',
                textAlign: 'center'
            }}>
                Waiting for Other Annotators
            </h3>
            <div style={{
                color: '#94A3B8',
                textAlign: 'center',
                fontSize: '0.875rem',
                maxWidth: '280px',
                margin: '0 auto'
            }}>
                <div className="mb-2">Your measurement:</div>
                <div style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(45deg, #60A5FA, #A78BFA)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}>
                    {systolic}/{diastolic} mmHg
                </div>
            </div>
        </div>
    );


    return (
        <React.Fragment >
        <div style={{
            minHeight: '100vh',
            width: '100vw',
            backgroundColor: '#0F172A',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            overscrollBehavior: 'none'
        }}>
            <Container fluid style={{ maxWidth: '500px' }}>
                <Card style={{
                    backgroundColor: '#1E293B',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    <CardBody style={{ padding: '12px' }}>
                        {showResult ? renderResult() :
                            status === 'measuring' ? renderMeasurement() :
                            status === 'waiting_others' ? renderWaitingOthers() :
                            renderWaitingState()}
                    </CardBody>
                </Card>
            </Container>
        </div>
         <OverwriteManagerModal
         isOpen={overwriteManagerModalOpen}
         toggle={() => setOverwriteManagerModalOpen(false)}
         onConfirm={handleOverwriteConfirm}
         close={closeYourself}
       />
       </React.Fragment >
    );

};

export default BloodPressureAnnotation;